import { useEffect, useRef } from 'react';
import apiClient from '../config/axios';
import { useAuth } from '../context/AuthContext';

/**
 * Record a detail-page view in the signed-in user's recently-viewed history
 * (issue 157). Fire-and-forget: history is decorative, so the POST is never
 * awaited by anything, never blocks or delays a render, and errors are
 * swallowed — a failed write costs a history line, not a page.
 *
 * Guests are skipped entirely (no pointless 401 per page view), and `ready`
 * must only be true on a SUCCESSFUL load — a 404 or a cross-system redirect is
 * not a view. The ref dedupes within a mount so a re-render that re-toggles
 * `ready` (e.g. pinning a different printing of the same entity) does not
 * re-post; navigating away and back remounts, which is a genuine re-view and
 * bumps the row.
 */
export default function useRecordView(entityType, slug, ready) {
  const { isAuthenticated } = useAuth();
  const lastRecorded = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !ready || !slug) return;
    const key = `${entityType}:${slug}`;
    if (lastRecorded.current === key) return;
    lastRecorded.current = key;
    apiClient
      .post('/api/recently-viewed', { entity_type: entityType, slug })
      .catch(() => {});
  }, [isAuthenticated, ready, entityType, slug]);
}
